<template>
    <div ref="meterEl" class="inline-flex items-center justify-center" :style="{ width: width + 'px' }">
        <svg viewBox="0 0 200 115" class="w-full h-auto">
            <!-- Colored arc segments -->
            <path
                v-for="(segment, i) in arcSegments"
                :key="'arc-' + i"
                :d="segment.d"
                :fill="segment.color"
                stroke="white"
                stroke-width="2"
            />

            <!-- Needle -->
            <g transform="translate(100, 100)">
                <g
                    class="risk-needle"
                    :class="{ 'risk-needle--animated': isAnimated }"
                    :style="{ transform: `rotate(${displayAngle}deg)` }"
                >
                    <path d="M 0,-3.5 L 64,-0.8 L 70,0 L 64,0.8 L 0,3.5 Z" fill="#374151" />
                </g>
            </g>

            <!-- Center cap -->
            <circle cx="100" cy="100" r="7" fill="#374151" />
            <circle cx="100" cy="100" r="3.5" fill="#9ca3af" />
        </svg>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'

const props = defineProps({
    addonCount: {
        type: Number,
        default: 0,
    },
    width: {
        type: Number,
        default: 80,
    },
    delay: {
        type: Number,
        default: 1500,
    },
})

const OUTER_R = 85
const INNER_R = 55
const CX = 100
const CY = 100

const ZONE_SPAN = 180 / 7
const zones = [
    { start: 6 * ZONE_SPAN, end: 7 * ZONE_SPAN, color: '#15803d' },
    { start: 5 * ZONE_SPAN, end: 6 * ZONE_SPAN, color: '#16a34a' },
    { start: 4 * ZONE_SPAN, end: 5 * ZONE_SPAN, color: '#65a30d' },
    { start: 3 * ZONE_SPAN, end: 4 * ZONE_SPAN, color: '#eab308' },
    { start: 2 * ZONE_SPAN, end: 3 * ZONE_SPAN, color: '#f59e0b' },
    { start: 1 * ZONE_SPAN, end: 2 * ZONE_SPAN, color: '#ea580c' },
    { start: 0 * ZONE_SPAN, end: 1 * ZONE_SPAN, color: '#dc2626' },
]

const angleForCount = (count) => -(ZONE_SPAN / 2 + ZONE_SPAN * Math.max(0, Math.min(5, count)))

const meterEl = ref(null)
const isAnimated = ref(false)
const hasEnteredView = ref(false)
const displayAngle = ref(angleForCount(0))
const targetAngle = computed(() => angleForCount(props.addonCount))

let observer = null
let delayTimer = null

const triggerAnimation = () => {
    delayTimer = setTimeout(() => {
        isAnimated.value = true
        displayAngle.value = targetAngle.value
    }, props.delay)
}

onMounted(() => {
    observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && !hasEnteredView.value) {
                hasEnteredView.value = true
                triggerAnimation()
                observer.disconnect()
            }
        },
        { threshold: 0.3 }
    )

    if (meterEl.value) {
        observer.observe(meterEl.value)
    }
})

onBeforeUnmount(() => {
    if (observer) observer.disconnect()
    if (delayTimer) clearTimeout(delayTimer)
})

watch(targetAngle, (val) => {
    if (!hasEnteredView.value) return
    isAnimated.value = true
    displayAngle.value = val
})

function segmentPath(startDeg, endDeg) {
    const sr = (startDeg * Math.PI) / 180
    const er = (endDeg * Math.PI) / 180
    const la = (endDeg - startDeg) > 180 ? 1 : 0

    const ox = (a) => CX + OUTER_R * Math.cos(a)
    const oy = (a) => CY - OUTER_R * Math.sin(a)
    const ix = (a) => CX + INNER_R * Math.cos(a)
    const iy = (a) => CY - INNER_R * Math.sin(a)

    return [
        `M${ox(sr).toFixed(2)},${oy(sr).toFixed(2)}`,
        `A${OUTER_R},${OUTER_R} 0 ${la} 0 ${ox(er).toFixed(2)},${oy(er).toFixed(2)}`,
        `L${ix(er).toFixed(2)},${iy(er).toFixed(2)}`,
        `A${INNER_R},${INNER_R} 0 ${la} 1 ${ix(sr).toFixed(2)},${iy(sr).toFixed(2)}`,
        'Z',
    ].join(' ')
}

const arcSegments = computed(() =>
    zones.map((z) => ({ d: segmentPath(z.start, z.end), color: z.color }))
)
</script>

<style scoped>
.risk-needle {
    transform-origin: 0 0;
}

.risk-needle--animated {
    transition: transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>
